import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminAuth } from '@/services/adminAuth';
import { toast } from 'sonner';
import { Users, Trash2, Shield, User } from 'lucide-react';

export default function AdminSettings() {
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'super'>('admin');

  const loadAdmins = async () => {
    const result = await adminAuth.listAdmins();
    if (result.success && result.admins) {
      setAdmins(result.admins);
    } else {
      toast.error(result.error || '获取管理员列表失败');
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newPassword) {
      toast.error('用户名和密码不能为空');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('密码至少6位');
      return;
    }

    setLoading(true);
    const result = await adminAuth.addAdmin(
      newUsername,
      newPassword,
      newName || newUsername,
      newRole
    );

    if (result.success) {
      toast.success('管理员添加成功');
      setNewUsername('');
      setNewName('');
      setNewPassword('');
      setNewRole('admin');
      setShowAddForm(false);
      loadAdmins();
    } else {
      toast.error(result.error || '添加失败');
    }
    setLoading(false);
  };

  const handleRemove = async (username: string) => {
    if (!confirm(`确定要删除管理员 ${username} 吗？`)) return;

    setLoading(true);
    const result = await adminAuth.removeAdmin(username);
    if (result.success) {
      toast.success('已删除');
      loadAdmins();
    } else {
      toast.error(result.error || '删除失败');
    }
    setLoading(false);
  };

  const currentAdmin = adminAuth.getAdminInfo();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          <Users className="h-6 w-6 mr-2" />
          管理员设置
        </h1>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '取消' : '添加管理员'}
        </Button>
      </div>

      {/* 添加表单 */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>添加新管理员</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>用户名</Label>
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="登录用户名"
                    required
                  />
                </div>
                <div>
                  <Label>显示名称</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="显示名称（可选）"
                  />
                </div>
                <div>
                  <Label>密码</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="至少6位"
                    required
                  />
                </div>
                <div>
                  <Label>角色</Label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as 'admin' | 'super')}
                    className="w-full h-10 px-3 border rounded-md"
                  >
                    <option value="admin">普通管理员（仅操作数据）</option>
                    <option value="super">超级管理员（可管理其他管理员）</option>
                  </select>
                </div>
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? '添加中...' : '确认添加'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* 管理员列表 */}
      <Card>
        <CardHeader>
          <CardTitle>当前管理员列表</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {admins.map((admin) => (
              <div
                key={admin.username}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    {admin.role === 'super' ? (
                      <Shield className="h-5 w-5 text-purple-600" />
                    ) : (
                      <User className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{admin.name || admin.username}</p>
                    <p className="text-sm text-gray-500">用户名: {admin.username}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      admin.role === 'super'
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}
                  >
                    {admin.role === 'super' ? '超级管理员' : '管理员'}
                  </span>
                  {currentAdmin?.username === admin.username && (
                    <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">当前用户</span>
                  )}
                </div>

                {admin.username !== currentAdmin?.username && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(admin.username)}
                    disabled={loading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {admins.length === 0 && <p className="text-gray-500 text-center py-8">暂无管理员数据</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
